import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";

type Row = {
  user_id: string;
  enrolled: boolean;
  enrolled_at: string | null;
  last_verified_at: string | null;
  recovery_reset_at: string | null;
};

export default function AdminMfaPanel() {
  const [rows, setRows] = useState<Row[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("user_mfa_status")
        .select("user_id, enrolled, enrolled_at, last_verified_at, recovery_reset_at")
        .order("enrolled_at", { ascending: false, nullsFirst: false })
        .limit(500);
      if (error) throw error;
      setRows((data ?? []) as Row[]);
    } catch (err: any) {
      toast({ title: "Failed to load MFA users", description: err?.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => !search || r.user_id.includes(search));

  const onConfirmReset = async () => {
    if (!target || !reason.trim()) return;
    setBusy(true);
    try {
      const { error } = await supabase.functions.invoke("admin-mfa-reset", {
        body: { target_user_id: target, reason: reason.trim() },
      });
      if (error) throw error;
      toast({ title: "MFA reset", description: "User must re-enroll on next sign-in." });
      setTarget(null);
      setReason("");
      await load();
    } catch (err: any) {
      toast({ title: "Reset failed", description: err?.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>MFA Recovery</CardTitle>
        <CardDescription>
          Reset a user's two-factor authentication if they have lost access to their authenticator.
          This deletes all MFA factors for the user — they will be prompted to enroll again on next sign-in.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input placeholder="Search by user id…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Button variant="outline" onClick={load} disabled={loading}>Refresh</Button>
        </div>

        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-3 py-2">User ID</th>
                <th className="text-left px-3 py-2">Enrolled</th>
                <th className="text-left px-3 py-2">Last verified</th>
                <th className="text-left px-3 py-2">Last reset</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No MFA records yet.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.user_id} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{r.user_id}</td>
                  <td className="px-3 py-2">{r.enrolled ? "Yes" : "No"}</td>
                  <td className="px-3 py-2">{r.last_verified_at ? new Date(r.last_verified_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-2">{r.recovery_reset_at ? new Date(r.recovery_reset_at).toLocaleString() : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => setTarget(r.user_id)}>Reset MFA</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={!!target} onOpenChange={(v) => { if (!v) { setTarget(null); setReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset MFA for user</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">User: <span className="font-mono">{target}</span></p>
            <div className="space-y-2">
              <Label htmlFor="reset-reason">Reason (required, audited)</Label>
              <Input id="reset-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Lost device, verified identity over phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={onConfirmReset} disabled={busy || !reason.trim()}>
              {busy ? "Resetting…" : "Confirm reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
